import * as bs from 'bootstrap';
import * as models from 'smg/customer-api/models';
import { OrderAggregates, Orders } from 'smg/customer-api/orders';
import { ShowError } from 'smg/errors';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import { AccountDetailsInit, PageLoader, RequireSession } from '../../account';
import i18n from '../../i18n';
import { DrawOrderCard } from '../../order';
import { CountOrders, ListOrders, OrderState, OrderStateByKey, OrderStates, SearchTerm } from '../../order-states';

void main();

const pageLoaders = new Map<string, PageLoader<models.HyejooOrder>>();
let currentPageLoader: PageLoader<models.HyejooOrder> | null = null;
const pageMax = 5;

const orderListData =
    'id,invoiceNumber,created,orderLineItems,orderProofs,orderShippingOptions,' +
    'orderPayments,total,paid,balance,totalSansTax,orderStatusID,userTaxExemptionOrders,shipmentsCount,paidByPurchaseOrder,accountState,reviewLink';

type stateKind = 'empty' | 'emptyStatus' | 'noMatches' | 'error';

interface stateCopy {
    icon: string;
    heading: string;
    body: string;
    action?: string;
}

// The page is always in exactly one of these when it has nothing to list, and
// each says a different true thing: never ordered, nothing in this status,
// nothing matched the search, or the list did not load.
function drawState($parent: HTMLElement, kind: stateKind, retry?: () => void) {
    const copy: Record<stateKind, stateCopy> = {
        empty: { icon: 'receipt_long', heading: i18n('ordersEmptyHeading'), body: i18n('ordersEmptyBody'), action: '#orders-empty-template' },
        emptyStatus: { icon: 'filter_alt_off', heading: i18n('ordersEmptyStatusHeading'), body: i18n('ordersEmptyStatusBody') },
        noMatches: { icon: 'search_off', heading: i18n('ordersNoMatchesHeading'), body: i18n('ordersNoMatchesBody'), action: '#orders-no-matches-template' },
        error: { icon: 'cloud_off', heading: i18n('ordersErrorHeading'), body: i18n('ordersErrorBody'), action: '#orders-error-template' },
    };
    const c = copy[kind];

    $parent.replaceChildren();
    const $state = template.Clone('#orders-state-template').firstElementChild as HTMLElement;
    ($state.querySelector('.orders-state-icon') as HTMLElement).textContent = c.icon;
    ($state.querySelector('.orders-state-heading') as HTMLElement).textContent = c.heading;
    ($state.querySelector('.orders-state-body') as HTMLElement).textContent = c.body;
    if (c.action) {
        const $action = template.Clone(c.action).firstElementChild as HTMLElement;
        ($state.querySelector('.orders-state-action') as HTMLElement).appendChild($action);
    }

    $state.querySelector('.orders-clear-search')?.addEventListener('click', () => {
        const $search = document.querySelector<HTMLInputElement>('#account-search-input');
        const $form = document.querySelector<HTMLFormElement>('#account-page-searchbar');
        if ($search) {
            $search.value = '';
        }
        $form?.requestSubmit();
    });
    $state.querySelector('.orders-retry')?.addEventListener('click', () => retry?.());

    $parent.appendChild($state);
}

async function main() {
    if (await RequireSession()) {
        return;
    }

    await AccountDetailsInit();

    const allOrdersCount = OrderAggregates('', ['id']).then(resp => resp.aggregates.get('id')?.count ?? 0);

    const stateCounts = new Map<string, Promise<number>>();
    for (const state of OrderStates) {
        stateCounts.set(state.key, CountOrders(state));
    }

    const $statusNav = document.querySelector('#status-nav') as HTMLElement;
    const $tabPaneTriggers = Array.from(document.querySelectorAll<HTMLElement>('[data-bs-toggle="tab"]'));

    void Promise.all([allOrdersCount, ...stateCounts.values()]).then(async () => {
        $statusNav.classList.remove('d-none');

        const $allOrdersCount = document.querySelector('#all-orders-count') as HTMLElement;
        $allOrdersCount.textContent = (await allOrdersCount).toLocaleString();
        for (const state of OrderStates) {
            const $count = document.querySelector<HTMLElement>(`#${state.key}-orders-count`);
            if ($count) {
                const count = await stateCounts.get(state.key) ?? 0;
                $count.textContent = count.toLocaleString();
                // An empty filter stays clickable but reads quiet.
                $count.closest('.nav-link')?.classList.toggle('opacity-50', count === 0);
            }
        }
    });

    QueryString().delete('p');

    // A state tab's count is the whole status; while a search is active the
    // "Showing 5 of 17" line has to count the matches instead.
    async function matchCount(state: OrderState | undefined, extraQ: string): Promise<number | undefined> {
        if (!extraQ) {
            return undefined;
        }
        if (state) {
            return CountOrders(state, extraQ);
        }
        const resp = await OrderAggregates(extraQ, ['id']);
        return resp.aggregates.get('id')?.count ?? 0;
    }

    function createPl($parent: HTMLElement, total: number, state?: OrderState): PageLoader<models.HyejooOrder> {
        const $viewMore = template.Clone('#view-more-btn-template').firstElementChild as HTMLElement;
        const $viewMoreBtn = $viewMore.querySelector('#view-more-btn') as HTMLButtonElement;
        const $paging = $viewMore.querySelector('.orders-paging') as HTMLElement;

        let shownTotal = total;

        const pl: PageLoader<models.HyejooOrder> = new PageLoader<models.HyejooOrder>({
            clearResults: false,
            parent: $parent,
            nextBtn: $viewMoreBtn,
            navTabs: $tabPaneTriggers,
            beforeLoad: () => {
                if (pl.currentPage() === 0) {
                    $parent.replaceChildren();
                }
                $viewMore.remove();
            },
            get: async () => {
                const sort = pl.sort() || 'created desc';
                // Free text — the server's full-text search covers invoice,
                // PO, tracking, names and proofs.
                const extraQ = SearchTerm(pl.search());
                const counted = matchCount(state, extraQ);

                let rows: models.HyejooOrder[];
                let nextPage: boolean;
                if (state) {
                    const data = await ListOrders(state, orderListData, sort, {
                        max: pageMax,
                        p: pl.currentPage(),
                        extraQ,
                    });
                    rows = data.orders;
                    nextPage = data.nextPage;
                } else {
                    const data = await Orders({
                        sort,
                        max: pageMax,
                        data: orderListData,
                        p: pl.currentPage(),
                        q: extraQ,
                    });
                    rows = data.results;
                    nextPage = data.nextPage;
                }

                shownTotal = await counted ?? total;
                return { rows, nextPage };
            },
            draw: async order => await DrawOrderCard(order),
            afterLoad: (rows, nextPage) => {
                if (rows.length === 0 && pl.currentPage() === 0) {
                    if (pl.search()) {
                        drawState($parent, 'noMatches');
                    } else if (state) {
                        drawState($parent, 'emptyStatus');
                    } else {
                        drawState($parent, 'empty');
                    }
                    return;
                }

                const shown = Math.min((pl.currentPage() + 1) * pageMax, shownTotal);
                $paging.textContent = i18n('ordersPagingCount').replace('%s', shown.toLocaleString()).replace('%s', shownTotal.toLocaleString());
                $viewMoreBtn.classList.toggle('d-none', !nextPage);
                $parent.appendChild($viewMore);
            },
            onError: err => {
                ShowError(err);
                drawState($parent, 'error', () => pl.load());
            },
        });
        return pl;
    }

    async function activatePane(target: string) {
        const $tabPane = document.querySelector(target) as HTMLElement | null;
        const $parent = $tabPane?.querySelector('.inner-container') as HTMLElement | undefined;
        if (!$parent) {
            return;
        }

        if (currentPageLoader) {
            currentPageLoader.deactivate();
        }

        let pl = pageLoaders.get(target);
        if (!pl) {
            QueryString().delete('p');
            if (target === '#all-orders-tab-pane') {
                pl = createPl($parent, await allOrdersCount);
            } else {
                const key = target.replace(/^#/, '').replace(/-tab-pane$/, '');
                const state = OrderStateByKey(key);
                if (!state) {
                    return;
                }
                pl = createPl($parent, await stateCounts.get(state.key) ?? 0, state);
            }
            pageLoaders.set(target, pl);
        }

        currentPageLoader = pl;
        currentPageLoader.activate();
        currentPageLoader.load();
    }

    for (const $tabPaneTrigger of $tabPaneTriggers) {
        $tabPaneTrigger.addEventListener('show.bs.tab', () => {
            const target = $tabPaneTrigger.getAttribute('data-bs-target');
            if (!target) {
                return;
            }
            QueryString().delete('p');
            const key = target.replace(/^#/, '').replace(/-tab-pane$/, '');
            if (key === 'all-orders') {
                QueryString().delete('status');
            } else {
                QueryString().set('status', key);
            }
            void activatePane(target);
        });
    }

    const statusKey = QueryString().get('status');
    const deepLink = statusKey ? OrderStateByKey(statusKey) : undefined;
    if (deepLink) {
        const $tab = document.querySelector<HTMLElement>(`#${deepLink.key}-tab`);
        if ($tab) {
            bs.Tab.getOrCreateInstance($tab).show();
            return;
        }
    }

    await activatePane('#all-orders-tab-pane');
}
